/**
 * Image resizing utilities for the browser
 * Handles multiple image sizes for different use cases
 */

export interface ImageSize {
  suffix: string;
  maxWidth: number;
  maxHeight: number;
  quality: number;
}

export interface ResizedImage {
  suffix: string;
  file: File;
  url: string;
}

// Standard image sizes for the application
export const IMAGE_SIZES: ImageSize[] = [
  { suffix: 'thumbnail', maxWidth: 150, maxHeight: 150, quality: 0.8 },
  { suffix: 'small', maxWidth: 400, maxHeight: 400, quality: 0.85 },
  { suffix: 'medium', maxWidth: 800, maxHeight: 800, quality: 0.9 },
  { suffix: 'large', maxWidth: 1200, maxHeight: 1200, quality: 0.9 },
  { suffix: 'original', maxWidth: 4096, maxHeight: 4096, quality: 0.95 }
];

/**
 * Calculate dimensions while maintaining aspect ratio
 */
function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  const aspectRatio = originalWidth / originalHeight;

  let width = originalWidth;
  let height = originalHeight;

  // Scale down if image is larger than max dimensions
  if (width > maxWidth) {
    width = maxWidth;
    height = width / aspectRatio;
  }

  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }

  return {
    width: Math.round(width),
    height: Math.round(height)
  };
}

/**
 * Resize a single image to specified dimensions
 */
async function resizeSingleImage(
  file: File,
  size: ImageSize
): Promise<ResizedImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        const { width, height } = calculateDimensions(
          img.width,
          img.height,
          size.maxWidth,
          size.maxHeight
        );

        // Create canvas for resizing
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Use better image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob'));
              return;
            }
            
            // Create file from blob
            const resizedFile = new File(
              [blob],
              `${file.name.split('.')[0]}_${size.suffix}.${file.type.split('/')[1]}`,
              { type: file.type }
            );
            
            // Create object URL for preview
            const url = URL.createObjectURL(blob);
            
            resolve({
              suffix: size.suffix,
              file: resizedFile,
              url
            });
          },
          file.type,
          size.quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
}

/**
 * Resize an image to multiple sizes
 */
export async function resizeImageToMultipleSizes(
  file: File,
  sizes: ImageSize[] = IMAGE_SIZES
): Promise<ResizedImage[]> {
  const resizedImages: ResizedImage[] = [];
  
  for (const size of sizes) {
    try {
      const resized = await resizeSingleImage(file, size);
      resizedImages.push(resized);
    } catch (error) {
      console.error(`Failed to resize image to ${size.suffix}:`, error);
      // For original size, include the original file
      if (size.suffix === 'original') {
        resizedImages.push({
          suffix: 'original',
          file,
          url: URL.createObjectURL(file)
        });
      }
    }
  }
  
  return resizedImages;
}

/**
 * Clean up object URLs to prevent memory leaks
 */
export function cleanupImageUrls(images: ResizedImage[]): void {
  images.forEach(image => {
    if (image.url && image.url.startsWith('blob:')) {
      URL.revokeObjectURL(image.url);
    }
  });
}

/**
 * Validate image file
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
  const maxSize = 10 * 1024 * 1024; // 10MB

  if (!allowedTypes.includes(file.type)) {
    return { 
      valid: false, 
      error: `Invalid file type. Allowed types: ${allowedTypes.join(', ')}` 
    };
  }

  if (file.size > maxSize) {
    return { 
      valid: false, 
      error: `File too large. Maximum size: ${maxSize / (1024 * 1024)}MB` 
    };
  }

  return { valid: true };
}